/**
 * Session UI state of the Lines feature (W2-07 lines-panel), shared by the panel, the commands, the status-bar host
 * and the preview overlay: view (tree / export order), collapsed tree nodes, filter chips, the open internal dialog
 * (line switcher, parent picker, New N lines, groups editor, image picker, HUD element picker), the Ctrl+wheel
 * accumulator and the hand-off between the Lines parent flow and the parent prompt.
 *
 * One store per app: the store is keyed on `services.editor` (stable for the app; command contexts are spread copies
 * of the services, so the services object itself is not a stable key). It is reset when another project opens
 * (collapsed nodes and filters name line ids of the previous chart). The view mode is a user preference stored in
 * `settings.ext['lines.view']`; nothing else here is persisted (preview filters live in the editor store and are
 * persisted per project there).
 */

import type { AppServices } from '../../app/registry/types';
import type { LinePreset } from '../../core/commands/types';
import type { LineId, ProjectId } from '../../core/model/types';
import type { Unsubscribe } from '../../core/util/types';
import type { EditorStoreApi } from '../../state/types';
import { NO_FILTERS, type LineFilterState } from './lineModel';

export type LinesView = 'tree' | 'order';

/** Settings key (ext) of the view preference. */
export const VIEW_SETTING = 'lines.view';

/** What the line picker is for. */
export type PickerMode =
  | { readonly kind: 'activate' }
  /** Choose the parent of `lines` (lines that would loop are listed greyed out). */
  | { readonly kind: 'parent'; readonly lines: readonly LineId[] }
  /** Add a line whose notes the lane shows as ghosts ("Also show"). */
  | { readonly kind: 'alsoShow' };

export type LinesModal =
  | { readonly kind: 'picker'; readonly mode: PickerMode }
  | { readonly kind: 'newLines'; readonly preset: LinePreset }
  | { readonly kind: 'groups' }
  /** Image or GIF line: choose the picture first. */
  | { readonly kind: 'image'; readonly gif: boolean }
  /** UI mover line: choose the HUD element first. */
  | { readonly kind: 'uiElement' };

export interface LinesUiState {
  readonly projectId: ProjectId | null;
  readonly collapsed: ReadonlySet<LineId>;
  readonly filters: LineFilterState;
  readonly modal: LinesModal | null;
  /** A row the panel should scroll to (set by commands that create or reveal lines). */
  readonly reveal: { readonly line: LineId; readonly seq: number } | null;
  /** Line whose name is being edited in the panel (F2, double-click, "Rename"). */
  readonly renaming: LineId | null;
}

/**
 * Hand-off between the Lines parent flow (`setParentFlow`) and `DIALOG.parentPrompt`. The prompt's contracted props
 * name one line and its result is only the mode (change request CR-2), so the flow leaves its whole request here
 * while the prompt is open. The prompt recognises it (same line and parent), lists every line of the request, and
 * hands a changed "Also turn with parent" back through `answerTurn` instead of applying it; the flow applies it to
 * every line in the same transaction as the parent change. Prompts opened by other packages find no matching
 * request and apply the switch themselves.
 */
export interface ParentRequest {
  readonly line: LineId;
  readonly parent: LineId | null;
  /** Every line whose parent changes (export order). */
  readonly lines: readonly LineId[];
  /** "Also turn with parent" chosen in the prompt for every line; null = leave each line as it is. */
  readonly answerTurn: (turn: boolean | null) => void;
}

export interface LinesUi {
  get(): LinesUiState;
  set(patch: Partial<LinesUiState>): void;
  /** Replace the state without notifying (project switch noticed during a render). */
  reset(patch: Partial<LinesUiState>): void;
  subscribe(cb: () => void): Unsubscribe;
  /** Fractional Ctrl+wheel notches not yet turned into a line switch (trackpads send small deltas). */
  wheelRest: number;
  /** The Lines parent flow's request while its parent prompt is open. */
  parentRequest: ParentRequest | null;
}

const INITIAL: LinesUiState = {
  projectId: null,
  collapsed: new Set(),
  filters: NO_FILTERS,
  modal: null,
  reveal: null,
  renaming: null,
};

function createLinesUi(): LinesUi {
  let state = INITIAL;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set(patch) {
      state = { ...state, ...patch };
      for (const cb of [...listeners]) cb();
    },
    reset(patch) {
      state = { ...state, ...patch };
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    wheelRest: 0,
    parentRequest: null,
  };
}

const stores = new WeakMap<EditorStoreApi, LinesUi>();

/** The Lines UI store of an app (created on first use); reset when another project is open. */
export function linesUi(services: Pick<AppServices, 'editor' | 'store'>): LinesUi {
  let ui = stores.get(services.editor);
  if (ui === undefined) {
    ui = createLinesUi();
    stores.set(services.editor, ui);
  }
  const project = services.store?.projectId ?? null;
  const cur = ui.get();
  if (cur.projectId !== project) {
    ui.reset({
      projectId: project,
      collapsed: new Set(),
      filters: NO_FILTERS,
      modal: null,
      reveal: null,
      renaming: null,
    });
    ui.wheelRest = 0;
    ui.parentRequest = null;
  }
  return ui;
}

let revealSeq = 0;

/** Ask the panel to scroll to a line (and open its collapsed ancestors). */
export function requestReveal(ui: LinesUi, line: LineId): void {
  revealSeq++;
  ui.set({ reveal: { line, seq: revealSeq } });
}

/** The view preference ('tree' unless the user chose the export order). */
export function viewOf(services: Pick<AppServices, 'settings'>): LinesView {
  return services.settings.getExt(VIEW_SETTING) === 'order' ? 'order' : 'tree';
}
